import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import ConciergeBand from "../components/ConciergeBand";

export default function Home() {
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/articles").then(r => setArticles(r.data)).catch(() => {});
  }, []);

  const hero = articles.find(a => !a.no_feature) || articles[0];
  const rest = articles.filter(a => a !== hero);

  // Issue label derived from the latest article: issue number = count of distinct YYYY-MM
  // periods in the archive, season + year from the latest article's published date.
  const issueLabel = (() => {
    if (!hero) return "";
    const months = { january: 1, february: 2, march: 3, april: 4, may: 5, june: 6, july: 7, august: 8, september: 9, october: 10, november: 11, december: 12 };
    const seasonOf = (m) => {
      if (m === 12 || m <= 2) return "Winter";
      if (m <= 5) return "Spring";
      if (m <= 8) return "Summer";
      return "Autumn";
    };
    const periods = new Set(articles.map(a => {
      const [mn, y] = (a.published || "").trim().split(" ");
      const m = months[(mn || "").toLowerCase()];
      return m && y ? `${y}-${m.toString().padStart(2, "0")}` : null;
    }).filter(Boolean));
    const issueNo = periods.size.toString().padStart(2, "0");
    const [mn, y] = hero.published.trim().split(" ");
    const m = months[(mn || "").toLowerCase()];
    // Winter rolls into next year (e.g. December 2025 = Winter 2025-26 → display 2026)
    const seasonYear = m === 12 ? Number(y) + 1 : y;
    return `Issue No. ${issueNo} · ${seasonOf(m)} ${seasonYear}`;
  })();

  return (
    <main data-testid="home-page">
      <Seo path="/" />
      {/* HERO */}
      <section className="border-b border-line">
        <div className="max-w-8xl mx-auto px-6 md:px-12 pt-6 md:pt-10 pb-12 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7 flex flex-col justify-between">
            <div>
              <div className="overline text-olive mb-6" data-testid="hero-issue-label">{issueLabel}</div>
              <h1 className="font-serif text-[3rem] sm:text-[4.5rem] md:text-[6rem] leading-[0.92] tracking-tight">
                More than a<br/>travel guide.
              </h1>
              <p className="mt-8 max-w-xl text-lg text-muted leading-relaxed">
                The Where To Go is a documentary-style travel platform featuring cinematic videos, destination guides, curated photography and cultural storytelling.
              </p>
              {hero && (
                <Link to={`/articles/${hero.slug}`} className="block mt-10 border-l-4 border-olive pl-6 group" data-testid="hero-article-callout">
                  <div className="overline text-olive mb-3">This issue's feature · {hero.destination}</div>
                  <div className="font-serif text-4xl md:text-5xl leading-[1.02] text-ink group-hover:text-olive transition-colors" data-testid="hero-article-title">{hero.title}</div>
                  <p className="mt-5 max-w-xl text-[1.05rem] text-ink/85 leading-relaxed" data-testid="hero-article-excerpt">
                    {hero.excerpt}
                  </p>
                </Link>
              )}
            </div>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/destinations" className="btn-primary inline-flex items-center gap-2" data-testid="hero-cta-destinations">Browse destinations <ArrowUpRight size={16}/></Link>
              <Link to={hero ? `/articles/${hero.slug}` : "/destinations"} className="btn-outline" data-testid="hero-cta-feature">Read the feature</Link>
            </div>
          </div>
          <div className="md:col-span-5">
            {hero ? (
              <Link to={`/articles/${hero.slug}`} className="block group" data-testid={`hero-card-${hero.slug}`}>
                <div className="aspect-square overflow-hidden">
                  <img src={hero.card_cover || hero.cover} alt={hero.title} className="w-full h-full object-cover img-hover"/>
                </div>
                <div className="mt-5 flex items-center justify-between text-sm">
                  <div className="overline text-olive">{hero.destination} · Feature</div>
                  <div className="text-muted">{hero.published}</div>
                </div>
                <div className="font-serif text-3xl md:text-4xl mt-2 leading-tight group-hover:text-olive transition-colors">{hero.title}</div>
              </Link>
            ) : <div className="aspect-square bg-alt"/>}
          </div>
        </div>
      </section>

      {/* Manifesto strip */}
      <section className="border-b border-line bg-alt">
        <div className="max-w-8xl mx-auto px-6 md:px-12 py-8 md:py-10 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-3 overline text-olive">The brief</div>
          <div className="md:col-span-9 font-serif text-xl md:text-2xl leading-[1.45] text-ink/85 max-w-3xl">
            Travel is more than just a journey; it's a transformative experience that enriches your life in countless ways. Travel raises cultural awareness, broadens perspectives and enriches your life. It allows you to experience different cultures, traditions and ways of life.
          </div>
        </div>
      </section>

      {/* Article grid */}
      <section className="max-w-8xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline text-olive mb-3">Published</div>
            <h2 className="font-serif text-4xl md:text-5xl">Latest articles</h2>
          </div>
          <Link to="/destinations" className="text-sm uppercase tracking-[0.18em] font-semibold hover:text-olive">All →</Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
          {rest.map((a) => (
            <Link key={a.slug} to={`/articles/${a.slug}`} className="group block" data-testid={`home-article-card-${a.slug}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={a.card_cover || a.cover} alt={a.title} className="w-full h-full object-cover img-hover"/>
              </div>
              <div className="mt-5 flex items-center justify-between text-sm">
                <div className="overline text-olive">{a.destination} · {a.region}</div>
                <div className="text-muted">{a.published}</div>
              </div>
              <div className="font-serif text-3xl mt-2 leading-tight group-hover:text-olive transition-colors">{a.title}</div>
              <div className="text-muted mt-3 text-[0.95rem] leading-relaxed">{a.excerpt}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Concierge teaser */}
      <ConciergeBand/>
    </main>
  );
}
